import { useContext, useEffect, useState } from "react";
import { TAcquisitionState } from "./types";
import { AcquisitionStoreContext } from "./ModernAcquisitionProvider";

/**
 * Subscribe to changes for a single field in the modernAcquisitionProvider
 * @param fieldName - field to subscribe to
 * @returns {TAcquisitionState} the field subscribed to
 */
export const useFieldSubscription = (fieldName: keyof TAcquisitionState) => {
    const acquisitionStore = useContext(AcquisitionStoreContext);
    const [field, setField] = useState(acquisitionStore.getState()[fieldName]);

    useEffect(() => {
        const checkForUpdates = () => {
            if (JSON.stringify(acquisitionStore.getState()[fieldName]) !== JSON.stringify(field)) {
                setField(acquisitionStore.getState()[fieldName]);
            }
        }
        return acquisitionStore.subscribe(checkForUpdates);
    }, [acquisitionStore, fieldName]);

    return { field };
}

/**
 * Subscribe to changes for multiple fields in the modernAcquisitionProvider
 * @param fieldNames - fields to subscribe to
 * @returns {TAcquisitionState} fields subscribed to
 */
export const useMultiFieldSubscription = (fieldNames: Array<keyof TAcquisitionState>) => {
    const acquisitionStore = useContext(AcquisitionStoreContext);
    const [fields, setFields] = useState(fieldNames.map(fn => ({ [fn]: acquisitionStore.getState()[fn] })));

    useEffect(() => {
        const checkForUpdates = () => {
            const hasAnyFieldUpdate = fieldNames.some(fn => JSON.stringify(fields[fn]) !== JSON.stringify({ [fn]: acquisitionStore.getState()[fn] }));
            if (hasAnyFieldUpdate) {
                setFields(fieldNames.map(fn => ({ [fn]: acquisitionStore.getState()[fn] })));
            }
        }
        return acquisitionStore.subscribe(checkForUpdates);
    }, [acquisitionStore, fieldNames]);

    return fields;
}


/**
 * Subscribe to changes for all fields in the modernAcquisitionProvider
 * @returns {TAcquisitionState} fields subscribed to
 */
export const useAllFieldSubscription = () => {
    const acquisitionStore = useContext(AcquisitionStoreContext);
    const [fields, setFields] = useState(acquisitionStore.getState());

    useEffect(() => {
        const checkForUpdates = () => {
            if (JSON.stringify(fields) !== JSON.stringify(acquisitionStore.getState())) {
                setFields(acquisitionStore.getState());
            }
        }
        return acquisitionStore.subscribe(checkForUpdates);
    }, [acquisitionStore]);

    return { fields };
}